import {
  oneOf,
  func,
  arrayOf,
  instanceOf,
  bool,
  node,
  string,
  any,
} from 'prop-types';

import Menu from 'components/base/Menu/Menu';
import { ReactComponent as Chevron } from 'icons/icon-chevron.svg';
import metricPropType from 'propTypes/metric';
import metrics from 'maps/metrics';

import {
  StyledTableHead,
  Header,
  HeaderContent,
  HeaderButton,
  SortingControls,
  ButtonContent,
  ListItemContent,
  ListLabel,
  HeaderLabel,
  SubCell,
  Divider,
  MainLabel,
  StyledTableRow,
} from './DropdownHead.style';
import TippyToolTip from 'components/base/TippyToolTip/TippyToolTip';
import getTooltipContent from '../../../helpers/getTooltipContent';
import getPositionTrackerTooltipContent from 'components/data-containers/PositionsTable/PositionTracker/utils/getTooltipContent';
import CustomerIcon from 'components/base/CustomerIcon/CustomerIcon';
import Box from 'components/elements/Box';

export const HeaderWrapper = ({
  condition,
  children,
  tableName = '',
  columnName = '',
}) => {
  const tooltipContent =
    tableName === 'positionTracker'
      ? getPositionTrackerTooltipContent(columnName)
      : getTooltipContent(tableName);

  return condition && tooltipContent ? (
    <TippyToolTip
      enabled={condition}
      tooltipContent={tooltipContent}
      offset={[0, 25]}
      maxWidth="170px"
      withDashedLine={tableName === 'concerts'}
      placement="top"
    >
      {children}
    </TippyToolTip>
  ) : (
    children
  );
};

/**
 * Renders a list of headers with an optional dropdownlist for 'count' and 'change' sorting.
 * @param {array} props.headers the given headers
 * @param {element} props.children React children
 * @param {object} props.sortMetric the given sort mertic
 * @param {object} props.sortOrder the given sort order (asc or desc)
 * @param {func} props.setSortMetric the sort metric state change method
 * @param {func} props.setSortOrder the sort order state change method
 * @param {func} props.changeSortMetric function to switch sorting metric
 * @param {object} props.withChangeMetric A set of header names that will be rendered with the count and change dropdown
 * @param {object} props.hideVisually A set of header names that will be not be visual
 * @param {object} props.notSortable A set of header names that will be not be clickable
 * @param {bool} props.onlyAllowInitialSortOrder when true render only a change and count option for the given sortOrder (asc or desc), otherwise it renders one for each sortOrder type.
 * @param {string} props.tableName
 * @param {object} props.menuItems
 * @param {bool} props.redesignedTable for new tables
 * @param {bool} props.isAlignBottom for vertical=bottom
 */
function DropdownHead(props) {
  const {
    headers,
    sortMetric,
    sortOrder,
    setSortMetric,
    setSortOrder,
    changeSortMetric,
    withChangeMetric,
    hideVisually,
    notSortable,
    onlyAllowInitialSortOrder = false,
    children,
    doubleLineMetric = false,
    tableName,
    predefinedMenuItems,
    redesignedTable = false,
    isTopListPlaylist,
    isAlignBottom = false,
    onlyHightToLow = false,
    isHeadSticky = false,
  } = props;

  const headAlign =
    tableName === 'chart' ||
    tableName === 'topCharts' ||
    tableName === 'topCertifications' ||
    tableName === 'concerts'
      ? 'start'
      : 'end';

  const getTopListPlaylistMargins = (index) =>
    index === 0 ? 'auto' : index === 2 || index === 3 ? '0 0 0 auto' : '0';

  function findActiveItem(item) {
    if (!sortMetric) return undefined;
    const keys = [sortMetric.key, sortMetric.value];
    const sameSortOrder = item.sortOrder === sortOrder;
    const sameSortBy = keys.includes(item.sortMetric);

    return sameSortBy && sameSortOrder;
  }

  function switchActiveMetric(head) {
    if (onlyAllowInitialSortOrder) return setSortMetric(head);

    const isChange = head.change && sortMetric.key === head.change;
    const metric = isChange ? sortMetric : head;

    changeSortMetric(metric);
  }

  function handleMenuChange(option) {
    setSortMetric(metrics[option.sortMetric]);
    if (!onlyAllowInitialSortOrder) setSortOrder(option.sortOrder);
  }

  function getMenuItems(head) {
    if (head.value === 'twtdOfGlobal' || onlyHightToLow) {
      return [
        {
          label: 'High to Low',
          sortMetric: head.key,
          sortOrder: 'desc',
        },
        {
          label: 'Low to High',
          sortMetric: head.key,
          sortOrder: 'asc',
        },
      ];
    }
    if (
      tableName === 'chart' ||
      tableName === 'topCharts' ||
      tableName === 'topCertifications' ||
      tableName === 'positionTracker'
    ) {
      if (
        head.value === 'customerName' ||
        head.value === 'chartName' ||
        head.value === 'countryName' ||
        head.value === 'certification_country' ||
        head.value === 'productDisplayName'
      ) {
        return [
          {
            label: `${head.label} title (A-Z)`,
            sortMetric: head.key,
            sortOrder: tableName === 'chart' ? 'desc' : 'asc',
          },
          {
            label: `${head.label} title (Z-A)`,
            sortMetric: head.key,
            sortOrder: tableName === 'chart' ? 'asc' : 'desc',
          },
        ];
      }
      if (head.value === 'dateLastReached') {
        return [
          {
            label: 'Date last reached (Old to New)',
            sortMetric: head.key,
            sortOrder: 'desc',
          },
          {
            label: 'Date last reached (New to Old)',
            sortMetric: head.key,
            sortOrder: 'asc',
          },
        ];
      }
      return [
        {
          label: 'High to Low',
          sortMetric: head.key,
          sortOrder:
            head.value === 'totalNumberOfCountries' ||
            head.value === 'chartStreams'
              ? 'asc'
              : 'desc',
        },
        {
          label: 'Low to High',
          sortMetric: head.key,
          sortOrder:
            head.value === 'totalNumberOfCountries' ||
            head.value === 'chartStreams'
              ? 'desc'
              : 'asc',
        },
      ];
    }
    if (
      tableName === 'BrowseOpus' ||
      tableName === 'TopLists' ||
      tableName === 'trackCustomLists'
    ) {
      if (head.key.match('displayName')) {
        const label = head.label.replace(/ .*/, '');
        const res = [
          {
            label: `${label} (A-Z)`,
            sortMetric: head.key,
            sortOrder: 'asc',
          },
          {
            label: `${label} (Z-A)`,
            sortMetric: head.key,
            sortOrder: 'desc',
          },
        ];
        return label === 'track' || label === 'release'
          ? [
              ...res,
              {
                label: `Release Date (Old to New)`,
                sortMetric: 'releaseDate',
                sortOrder: 'asc',
              },
              {
                label: `Release Date (New to Old)`,
                sortMetric: 'releaseDate',
                sortOrder: 'desc',
              },
            ]
          : res;
      }

      if (!onlyAllowInitialSortOrder) {
        return [
          {
            label: 'High to Low',
            sortMetric: head.key,
            sortOrder: 'desc',
          },
          {
            label: 'Low to High',
            sortMetric: head.key,
            sortOrder: 'asc',
          },
        ];
      }
    }
    if (!onlyAllowInitialSortOrder) {
      return [
        {
          label: 'Count (Descending)',
          sortMetric: head.key,
          sortOrder: 'desc',
        },
        {
          label: 'Count (Ascending)',
          sortMetric: head.key,
          sortOrder: 'asc',
        },
        {
          label: 'Change (Descending)',
          sortMetric: head.change,
          sortOrder: 'desc',
        },
        {
          label: 'Change (Ascending)',
          sortMetric: head.change,
          sortOrder: 'asc',
        },
      ];
    }
    const label = sortOrder === 'desc' ? 'Descending' : 'Ascending';

    return [
      {
        label: `Count (${label})`,
        sortMetric: head.key,
        sortOrder,
      },
      {
        label: `Change (${label})`,
        sortMetric: head.change,
        sortOrder,
      },
    ];
  }

  return (
    <StyledTableHead tableName={tableName}>
      <StyledTableRow alignBottom={isAlignBottom} isHeadSticky={isHeadSticky}>
        {headers.map((head, index) => {
          const withDropdown = withChangeMetric.has(head.key);
          const multiline = doubleLineMetric;
          const hide = hideVisually && hideVisually.has(head.value);
          const notClickable =
            (head.value !== 'attendees' &&
              notSortable &&
              notSortable.has(head.value) &&
              !hide) ||
            isTopListPlaylist;
          const keys = sortMetric
            ? [sortMetric.key, sortMetric.change, sortMetric.count]
            : undefined;
          // All data related references are defined in `src/maps/metrics`.
          // When a count metric is selected (e.g 'This Week To Date (Count)'),
          // a `change` key (e.g 'performance_wow_last_completed') can exist if
          // the metric has a secondary trend metric. The value of this key is
          // the same as key in the API response to get the actual data value.
          //
          // This can also happen the other way around, when a change metric is
          // selected (e.g 'This Week To Date (Change)'), a `count` key can exist
          // to reference its complementary count metric.
          //
          // The table headers never change but the presence of either of these keys
          // can be used to compare it to the currently active sorting metric, which can
          // be a different object from `maps/metrics` than the ones used for the table headers,
          // to know which one is primary.
          const isName = sortMetric && sortMetric.value === head.value;
          const isActive =
            isName ||
            (tableName === 'BrowseOpus' || tableName === 'trackCustomLists'
              ? sortMetric &&
                (sortMetric.key === head.key ||
                  (sortMetric.key === 'releaseDate' &&
                    head.key === 'displayName'))
              : !redesignedTable &&
                keys &&
                keys.filter(Boolean).includes(head.key));
          const menuItems = predefinedMenuItems
            ? predefinedMenuItems[head.key]
            : getMenuItems(head);
          const selectedItem = menuItems.find(findActiveItem) || null;
          const itemToString = (item) => {
            if (item) {
              return `${item.sortMetric}-${item.sortOrder}`;
            }
            return false;
          };
          const isInPositionTrackerTable =
            head.value === 'official_local' ||
            head.value === 'radio' ||
            head.value === 'spotify' ||
            head.value === 'itunes' ||
            head.value === 'shazam';
          const isWrappedInTooltip =
            head.value === 'performance_this_week_to_date' ||
            head.value === 'countThisWeekToDate' ||
            head.value === 'twtd' ||
            head.value === 'attendees' ||
            isInPositionTrackerTable;

          const customerName = head.customerName;
          const subCells = head.subCells;

          const isFirstOfTracksIamWatching =
            index === 0 && tableName === 'Tracks I am Watching';

          const isRedesigned =
            tableName === 'playlists' && index !== 0 && index !== 1
              ? false
              : redesignedTable;
          let isMinWidth;
          if (tableName === 'playlists') {
            isMinWidth = index !== 0 && index !== 1 && headers.length < 8;
          }

          return (
            <Header
              data-hasdropdown={withDropdown}
              key={`browse-table-header-${head.value}`}
              id={head.value}
              scope="col"
              tableName={tableName}
              headerName={head?.value}
            >
              <SortingControls
                isActive={isActive && head.value !== 'official_local'}
                flex={headAlign}
                alignCenter={
                  tableName === 'Territory' && index !== headers.length - 1
                }
                redesignedTable={isRedesigned}
                isMinWidth={isMinWidth}
                margin={
                  isTopListPlaylist ? getTopListPlaylistMargins(index) : '0'
                }
                isFirstOfTracksIamWatching={isFirstOfTracksIamWatching}
              >
                {notClickable ? (
                  subCells ? (
                    <HeaderContent tableName={tableName}>
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          {customerName && (
                            <CustomerIcon
                              customer={customerName}
                              isDownloadsDsp={customerName === 'apple'} // for iTunes
                              showHiddenPlaceholder
                              small={true}
                            />
                          )}
                          <HeaderWrapper
                            condition={isWrappedInTooltip}
                            tableName={tableName}
                            columnName={head.value}
                          >
                            <MainLabel
                              withLeftMargin={customerName}
                              withDashedLine={isInPositionTrackerTable}
                            >
                              {head.value === 'twtdOfGlobal' ? (
                                <>% of Global &#10;&#13;this week to date</>
                              ) : (
                                head.label
                              )}
                            </MainLabel>
                          </HeaderWrapper>
                        </Box>
                        <Box display="flex" flexDirection="row">
                          {subCells.map((cell, index) => (
                            <SubCell key={`${index}_${head.label}`}>
                              {index !== 0 && <Divider />}
                              {cell}
                            </SubCell>
                          ))}
                        </Box>
                      </Box>
                    </HeaderContent>
                  ) : (
                    <HeaderContent tableName={tableName}>
                      {head.type !== 'trend' ? (
                        <HeaderLabel
                          textAlign={
                            head.value === 'countryId' &&
                            (tableName === 'playlists' ||
                              tableName === 'Tracks I am Watching') &&
                            !isTopListPlaylist
                              ? 'center'
                              : 'left'
                          }
                          tableName={tableName}
                        >
                          {head.label}
                        </HeaderLabel>
                      ) : (
                        <>
                          <HeaderLabel>{head.label}</HeaderLabel>
                          <span>{children}</span>
                        </>
                      )}
                    </HeaderContent>
                  )
                ) : (
                  <HeaderWrapper
                    condition={isWrappedInTooltip}
                    tableName={tableName}
                  >
                    <HeaderButton
                      tableName={tableName}
                      hideVisually={hide}
                      type="button"
                      multiline={multiline}
                      onClick={() => switchActiveMetric(head)}
                    >
                      <HeaderContent
                        multiline={multiline}
                        tableName={tableName}
                        twtdOfGlobal={head.value === 'twtdOfGlobal'}
                      >
                        {head.value === 'twtdOfGlobal' ? (
                          <>
                            % of Global <br />
                            this week to date
                          </>
                        ) : (
                          head.label
                        )}
                      </HeaderContent>
                    </HeaderButton>
                  </HeaderWrapper>
                )}
                {withDropdown && !isTopListPlaylist && (
                  <Menu
                    Item={MenuItem}
                    Button={MenuButton}
                    ListLabel={MenuListLabel}
                    onChange={(option) => handleMenuChange(option)}
                    selectedItem={selectedItem}
                    items={menuItems}
                    itemToString={itemToString}
                    firstItem={
                      head.key === 'displayName' || head.key === 'countryName'
                    }
                  />
                )}
              </SortingControls>
            </Header>
          );
        })}
      </StyledTableRow>
    </StyledTableHead>
  );
}

function MenuListLabel() {
  return <ListLabel>sort by</ListLabel>;
}

function MenuButton(props) {
  const { isOpen } = props;

  return (
    <ButtonContent isOpen={isOpen}>
      <Chevron aria-hidden className="chevron" />
    </ButtonContent>
  );
}

function MenuItem(props) {
  const { item, isActive } = props;

  return <ListItemContent isActive={isActive}>{item.label}</ListItemContent>;
}

DropdownHead.propTypes = {
  headers: arrayOf(metricPropType).isRequired,
  sortMetric: any,
  sortOrder: oneOf(['asc', 'desc']).isRequired,
  setSortMetric: func.isRequired,
  setSortOrder: func,
  changeSortMetric: func,
  withChangeMetric: instanceOf(Set).isRequired,
  hideVisually: instanceOf(Set),
  notSortable: instanceOf(Set),
  onlyAllowInitialSortOrder: bool,
  children: node,
  doubleLineMetric: bool,
  tableName: string,
  predefinedMenuItems: instanceOf(Object),
  redesignedTable: bool,
  isTopListPlaylist: bool,
  isAlignBottom: bool,
  onlyHightToLow: bool,
  isHeadSticky: bool,
};

export default DropdownHead;
